import {Injectable, signal} from '@angular/core';

/**
 * Service responsible for mobile device detection and mobile-specific audio optimizations
 * Extracted from AudioService to improve maintainability while preserving exact functionality
 */
@Injectable({
  providedIn: 'root'
})
export class AudioMobileService {

  // ============================================================================
  // MOBILE DEVICE STATE - EXACT preservation from AudioService
  // ============================================================================

  private isMobileDevice = signal<boolean>(this.detectMobileDevice());
  readonly isMobile = this.isMobileDevice.asReadonly();
  private bypassFadeCompletely = signal<boolean>(false);

  constructor() {
    console.log(`Device detected as ${this.isMobileDevice() ? 'mobile' : 'desktop'} platform`);

    // EXACT logic preservation: If on mobile, set initial flags to help with autoplay restrictions
    if (this.isMobileDevice()) {
      this.bypassFadeCompletely.set(true);
      console.log('Mobile device detected - enabling mobile-specific audio handling');
    }
  }

  // ============================================================================
  // MOBILE DEVICE DETECTION - EXACT preservation from AudioService lines 514-537
  // ============================================================================

  /**
   * Determine if fade effects should be bypassed
   * Used for mobile optimizations and user-initiated playback
   */
  shouldBypassFade(): boolean {
    // Only bypass fades if explicitly set or for mobile devices with autoplay restrictions
    return this.bypassFadeCompletely();
  }

  // ============================================================================
  // FADE BYPASS LOGIC - EXACT preservation
  // ============================================================================

  /**
   * Set bypass fade flag (used for user-initiated actions)
   */
  setBypassFadeCompletely(value: boolean): void {
    this.bypassFadeCompletely.set(value);
  }

  /**
   * Get current bypass fade state
   */
  getBypassFadeCompletely(): boolean {
    return this.bypassFadeCompletely();
  }

  /**
   * Handle autoplay restrictions on mobile devices
   * @param userInitiated Whether the action was directly initiated by user
   * @param lastUserInteractionTime Timestamp of last user interaction
   * @returns Whether autoplay is allowed
   */
  handleAutoplayRestrictions(userInitiated: boolean, lastUserInteractionTime: number): boolean {
    if (!this.isMobileDevice()) return true;

    const timeSinceLastInteraction = Date.now() - lastUserInteractionTime;
    const autoplayAllowed = userInitiated || timeSinceLastInteraction < 5000; // 5 second window

    if (!autoplayAllowed) {
      console.log('Mobile autoplay restriction - user interaction required');
      return false;
    }

    return true;
  }

  // ============================================================================
  // MOBILE-SPECIFIC AUTOPLAY HANDLING
  // ============================================================================

  /**
   * Check if device is iOS (iPhone, iPad, iPod)
   */
  isIOS(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(userAgent);
  }

  // ============================================================================
  // PLATFORM-SPECIFIC DETECTION METHODS
  // ============================================================================

  /**
   * Check if device is Android
   */
  isAndroid(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();
    return /android/.test(userAgent);
  }

  /**
   * Check if device is Windows Phone
   */
  isWindowsPhone(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();
    return /windows phone/.test(userAgent);
  }

  /**
   * Check if device is BlackBerry
   */
  isBlackBerry(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();
    return /blackberry/.test(userAgent);
  }

  /**
   * Check if direct volume application should be used (mobile optimization)
   */
  shouldUseDirectVolumeApplication(): boolean {
    return this.isMobileDevice();
  }

  // ============================================================================
  // MOBILE-SPECIFIC OPTIMIZATIONS
  // ============================================================================

  /**
   * Check if touch event optimization should be applied
   */
  optimizeForTouch(): boolean {
    return this.isMobileDevice() && 'ontouchstart' in window;
  }

  /**
   * Get recommended audio buffer size for mobile devices
   */
  getRecommendedBufferSize(): number {
    // Smaller buffer for mobile to reduce memory usage
    return this.isMobileDevice() ? 2048 : 4096;
  }

  /**
   * Check if video preload should be limited on mobile
   */
  shouldLimitVideoPreload(): boolean {
    return this.isMobileDevice();
  }

  /**
   * Get mobile-specific audio context options
   */
  getMobileAudioContextOptions(): AudioContextOptions | undefined {
    if (!this.isMobileDevice()) return undefined;

    return {
      sampleRate: 44100, // Standard rate that works well on mobile
      latencyHint: 'interactive' // Optimize for responsiveness
    };
  }

  /**
   * Detect if the current device is mobile (iOS, Android, BlackBerry, Windows Phone)
   * EXACT preservation of detection logic from AudioService
   */
  private detectMobileDevice(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();
    const mobileKeywords = [
      'iphone', 'ipad', 'ipod',           // iOS devices
      'android',                          // Android devices
      'blackberry',                       // BlackBerry devices
      'windows phone',                    // Windows Phone
      'mobile', 'tablet'                  // Generic mobile/tablet indicators
    ];

    // Check if any mobile keywords are present in the user agent
    const isMobile = mobileKeywords.some(keyword => userAgent.includes(keyword));

    // Additional check for touch capability (helps catch some edge cases)
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // Device is considered mobile if either user agent indicates mobile OR it has touch capability
    // but we're more conservative and primarily rely on user agent
    return isMobile || (hasTouch && userAgent.includes('mobile'));
  }
} 
